import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight } from "lucide-react";
import { EASE } from "../site/Reveal";

const FADE_HIDDEN = { opacity: 0 };
const FADE_VISIBLE = { opacity: 1 };
const RISE_HIDDEN = { opacity: 0, y: 24 };
const RISE_VISIBLE = { opacity: 1, y: 0 };
const IMG_HIDDEN = { opacity: 0, y: 50 };
const IMG_VISIBLE = { opacity: 1, y: 0 };
const CRUMBS_TRANSITION = { duration: 0.8, delay: 0.1 };
const OVERLINE_TRANSITION = { duration: 1, delay: 0.2 };
const TITLE_TRANSITION = { duration: 1, delay: 0.3, ease: EASE };
const INTRO_TRANSITION = { duration: 0.9, delay: 0.55, ease: EASE };
const CTA_TRANSITION = { duration: 0.9, delay: 0.75, ease: EASE };
const IMG_TRANSITION = { duration: 1.2, delay: 0.45, ease: EASE };
const NO_CRUMBS = [];

const Breadcrumbs = ({ crumbs }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-navy/55">
    <Link to="/" className="hover:text-navy transition-colors duration-300">Home</Link>
    {crumbs.map(({ label, path }) => (
      <span key={label} className="flex items-center gap-2">
        <ChevronRight size={12} strokeWidth={1.5} />
        {path ? <Link to={path} className="hover:text-navy transition-colors duration-300">{label}</Link> : <span className="text-navy">{label}</span>}
      </span>
    ))}
  </nav>
);

export default function PageHero({ overline, title, titleEm, intro, image, imageAlt, crumbs = NO_CRUMBS, onContact }) {
  return (
    <section data-testid="page-hero" className="relative pt-[76px] bg-ivory">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 lg:gap-10 items-center py-16 lg:py-24">
        <div>
          <motion.div initial={FADE_HIDDEN} animate={FADE_VISIBLE} transition={CRUMBS_TRANSITION}>
            <Breadcrumbs crumbs={crumbs} />
          </motion.div>
          <motion.p initial={FADE_HIDDEN} animate={FADE_VISIBLE} transition={OVERLINE_TRANSITION} className="mt-10 text-xs uppercase tracking-[0.3em] font-semibold text-navy/60 mb-6">
            {overline}
          </motion.p>
          <motion.h1
            data-testid="page-h1"
            initial={RISE_HIDDEN}
            animate={RISE_VISIBLE}
            transition={TITLE_TRANSITION}
            className="font-display text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight"
          >
            {title} {titleEm && <em className="italic font-medium">{titleEm}</em>}
          </motion.h1>
          <motion.p initial={RISE_HIDDEN} animate={RISE_VISIBLE} transition={INTRO_TRANSITION} className="mt-8 text-base md:text-lg leading-relaxed text-charcoal/85 max-w-xl">
            {intro}
          </motion.p>
          <motion.div initial={RISE_HIDDEN} animate={RISE_VISIBLE} transition={CTA_TRANSITION} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="page-hero-cta"
              onClick={onContact}
              className="group btn-shine inline-flex items-center gap-3 rounded-full bg-navy text-ivory px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-0.5"
            >
              Book a Free Call
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <Link
              to="/services"
              data-testid="page-hero-secondary-link"
              className="inline-flex items-center gap-3 rounded-full border border-navy/25 text-navy px-8 py-4 text-sm font-semibold tracking-wide transition-colors duration-300 hover:bg-navy/5"
            >
              All Services
            </Link>
          </motion.div>
        </div>
        {image && (
          <motion.div
            initial={IMG_HIDDEN}
            animate={IMG_VISIBLE}
            transition={IMG_TRANSITION}
            className="hidden md:block h-[420px] lg:h-[520px] overflow-hidden rounded-t-[120px] rounded-b-xl shadow-[0_30px_60px_rgba(62,74,46,0.2)]"
          >
            <img src={image} alt={imageAlt} className="w-full h-full object-cover" />
          </motion.div>
        )}
      </div>
    </section>
  );
}
